import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserCircle, ShieldCheck, LayoutDashboard, LogOut, ChevronRight, Settings, Info, LayoutGrid } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useConfig } from '../hooks/useConfig';
import { LogoutModal } from '../components/LogoutModal';
import { ICON_MAP } from '../constants';
import { cn } from '../lib/utils';
import { SignedImage } from '../components/SignedImage';

export const UserCategoryPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const { getTileStyles } = useConfig();
  const styles = getTileStyles();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = React.useState(false);

  const handleLogout = async () => {
    setIsLogoutModalOpen(false);
    await signOut();
  };

  // Define the requested items explicitly to ensure they are always present
  const items = [
    {
      item: 'Profilo',
      route: user?.personaId ? `/user/profilo/${user.personaId}` : '/user/profilo',
      icon: UserCircle,
      color: 'bg-blue-500/10 text-blue-500'
    },
    {
      item: 'Ruoli',
      route: '/user/ruoli',
      icon: ShieldCheck,
      color: 'bg-purple-500/10 text-purple-500'
    },
    {
      item: 'Mia Dashboard',
      route: '/user/dashboard',
      icon: LayoutDashboard,
      color: 'bg-indigo-500/10 text-indigo-500'
    },
    {
      item: 'Preferenze',
      route: '/user/preferenze',
      icon: Settings,
      color: 'bg-amber-500/10 text-amber-500'
    },
    {
      item: 'Google Workspace',
      route: '/user/workspace',
      icon: LayoutGrid,
      color: 'bg-emerald-500/10 text-emerald-500'
    },
    {
      item: 'Versione',
      route: '/user/versione',
      icon: Info,
      color: 'bg-orange-500/10 text-orange-500'
    },
    {
      item: 'Uscita',
      action: () => setIsLogoutModalOpen(true),
      icon: LogOut,
      color: 'bg-destructive/10 text-destructive'
    }
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemAnim = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Utente</h1>
        <p className="text-muted-foreground">Gestisci il tuo profilo e le tue preferenze.</p>
      </div>

      <motion.div 
        variants={container}
        initial="hidden"
        animate="show"
        className="flex flex-wrap"
        style={{ gap: styles.margin }}
      >
        {items.map((child, idx) => {
          const itemStyles = getTileStyles(child.item);
          const IconFromMenu = child.icon;
          const Icon = itemStyles.icon ? ICON_MAP[itemStyles.icon] : IconFromMenu;
          const iconPath = itemStyles.iconPath;
          
          const content = (
            <div 
              className="group flex flex-col items-center justify-center border hover:shadow-lg transition-all text-center rounded-2xl p-4 w-[var(--w-mobile)] h-[var(--h-mobile)] lg:w-[var(--w-desktop)] lg:h-[var(--h-desktop)] md:w-[var(--w-tablet)] md:h-[var(--h-tablet)] bg-[var(--bg-mobile)] md:bg-[var(--bg-tablet)] lg:bg-[var(--bg-desktop)]"
              style={{ 
                borderColor: itemStyles.bordo_sezione_colore || itemStyles.borderColor,
                borderWidth: itemStyles.bordo_sezione_spessore || itemStyles.borderSize,
                ['--w-desktop' as any]: itemStyles.desktop_larghezza,
                ['--h-desktop' as any]: itemStyles.desktop_altezza,
                ['--bg-desktop' as any]: itemStyles.desktop_sfondo,
                ['--w-tablet' as any]: itemStyles.tablet_larghezza,
                ['--h-tablet' as any]: itemStyles.tablet_altezza,
                ['--bg-tablet' as any]: itemStyles.tablet_sfondo,
                ['--w-mobile' as any]: itemStyles.larghezza_mobile,
                ['--h-mobile' as any]: itemStyles.altezza_mobile,
                ['--bg-mobile' as any]: itemStyles.mobile_sfondo,
                margin: itemStyles.margin
              }}
            >
              <div 
                className={cn(
                  "w-10 h-10 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform overflow-hidden shrink-0",
                  !iconPath && child.color && !itemStyles.sfondo_icona
                )}
                style={{
                  backgroundColor: itemStyles.sfondo_icona || undefined,
                  color: itemStyles.colore_icona || undefined
                }}
              >
                {child.item === 'Io' && user?.avatar_url && !iconPath ? (
                  <img 
                    src={user.avatar_url} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : iconPath ? (
                  <SignedImage 
                    path={iconPath} 
                    bucket="cpgev"
                    alt="" 
                    className="w-6 h-6 object-contain"
                  />
                ) : (
                  Icon && <Icon className="w-6 h-6" />
                )}
              </div>
              <h3 
                className="text-sm line-clamp-2 px-1 leading-tight flex-grow flex items-center justify-center transition-colors"
              >
                <span 
                  className="hidden lg:block w-full" 
                  style={{ 
                    color: itemStyles.desktop_colore, 
                    fontFamily: itemStyles.desktop_font, 
                    fontWeight: itemStyles.desktop_stile?.includes('bold') ? '700' : '400',
                    fontStyle: itemStyles.desktop_stile?.includes('italic') ? 'italic' : 'normal'
                  }}
                >
                  {child.item}
                </span>
                <span 
                  className="hidden md:block lg:hidden w-full" 
                  style={{ 
                    color: itemStyles.tablet_colore, 
                    fontFamily: itemStyles.tablet_font, 
                    fontWeight: itemStyles.tablet_stile?.includes('bold') ? '700' : '400',
                    fontStyle: itemStyles.tablet_stile?.includes('italic') ? 'italic' : 'normal'
                  }}
                >
                  {child.item}
                </span>
                <span 
                  className="md:hidden w-full" 
                  style={{ 
                    color: itemStyles.mobile_colore, 
                    fontFamily: itemStyles.mobile_font, 
                    fontWeight: itemStyles.mobile_stile?.includes('bold') ? '700' : '400',
                    fontStyle: itemStyles.mobile_stile?.includes('italic') ? 'italic' : 'normal'
                  }}
                >
                  {child.item}
                </span>
              </h3>
            </div>
          );

          return (
            <motion.div key={idx} variants={itemAnim}>
              {child.route ? (
                <Link to={child.route}>{content}</Link>
              ) : (
                <div onClick={child.action} className="cursor-pointer">{content}</div>
              )}
            </motion.div>
          );
        })}
      </motion.div>

      <LogoutModal 
        isOpen={isLogoutModalOpen} 
        onClose={() => setIsLogoutModalOpen(false)} 
        onConfirm={handleLogout} 
      />
    </div>
  );
};
